import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Image as ImageIcon,
  Calendar,
  User,
  Share2,
  Download,
  Check,
  ClipboardList,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI, commentsAPI } from '../../services/api';
import { Card, Button, StatusBadge } from '../ui';
import Loading from '../common/Loading';
import CommentsList from './CommentsList';
import {
  formatDate,
  handleApiError,
  getImageUrl,
  showToast,
  showConfirm,
  getMediaTypeFromUrl,
} from '../../utils/helpers';
import { PLATFORMS, CONTENT_TYPES } from '../../utils/platformConfig';
import { PlatformIcon } from '../icons/PlatformIcons';
import PlatformPreview from './previews';

const PostReviewLayoutC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isClient, isAdmin } = useAuth();
  const { isRTL } = useLanguage();

  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareUrlCopied, setShareUrlCopied] = useState(false);
  const [generatingShareLink, setGeneratingShareLink] = useState(false);
  const [changeRequestText, setChangeRequestText] = useState('');
  const [showChangeRequestModal, setShowChangeRequestModal] = useState(false);

  const fetchPostData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [postResponse, commentsResponse] = await Promise.all([
        postsAPI.getById(id),
        commentsAPI.getByPost(id),
      ]);

      const postData = postResponse.data.post;

      if (postData.current_image_url) {
        postData.current_image_url = getImageUrl(postData.current_image_url);
      }

      setPost(postData);
      setComments(commentsResponse.data.comments || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPostData();
  }, [id]);

  // Load existing share token if available
  useEffect(() => {
    if (post?.share_token) {
      const url = `${window.location.origin}/post/${post.share_token}`;
      setShareUrl(url);
    }
  }, [post?.share_token]);

  const handleApprove = async () => {
    const confirmed = await showConfirm(
      isRTL ? 'هل أنت متأكد من تعميد هذا البوست؟' : 'Are you sure you want to approve this post?',
      isRTL
    );

    if (!confirmed) return;

    try {
      setActionLoading(true);
      await postsAPI.approve(id);
      await fetchPostData();
      showToast.success(isRTL ? 'تم تعميد البوست بنجاح!' : 'Post approved successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestChanges = () => {
    setShowChangeRequestModal(true);
  };

  const handleSubmitChangeRequest = async () => {
    if (!changeRequestText.trim()) {
      showToast.error(
        isRTL ? 'يرجى كتابة التعديلات المطلوبة' : 'Please enter the changes needed'
      );
      return;
    }

    try {
      setActionLoading(true);
      await postsAPI.reject(id, { comment: changeRequestText.trim() });
      await fetchPostData();
      setShowChangeRequestModal(false);
      setChangeRequestText('');
      showToast.success(
        isRTL ? 'تم طلب التعديلات بنجاح!' : 'Changes requested successfully!'
      );
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelChangeRequest = () => {
    setShowChangeRequestModal(false);
    setChangeRequestText('');
  };

  const handleMarkAsReadyForReview = async () => {
    const confirmed = await showConfirm(
      isRTL
        ? 'هل أنت متأكد من أن البوست جاهز للمراجعة؟'
        : 'Are you sure this post is ready for review?',
      isRTL
    );

    if (!confirmed) {
      return;
    }

    try {
      setActionLoading(true);
      await postsAPI.update(id, { status: 'pending' });
      await fetchPostData();
      showToast.success(
        isRTL ? 'تم تحديث الحالة بنجاح!' : 'Post marked as ready for review!'
      );
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyShareLink = async () => {
    try {
      // إذا لم يكن الرابط موجوداً، قم بإنشائه أولاً
      if (!shareUrl) {
        setGeneratingShareLink(true);
        const response = await postsAPI.generateShareLink(id);
        const url = `${window.location.origin}/post/${response.data.share_token}`;
        setShareUrl(url);
        setGeneratingShareLink(false);

        await navigator.clipboard.writeText(url);
        setShareUrlCopied(true);
        setTimeout(() => setShareUrlCopied(false), 2000);
        return;
      }

      await navigator.clipboard.writeText(shareUrl);
      setShareUrlCopied(true);
      setTimeout(() => setShareUrlCopied(false), 2000);
    } catch (err) {
      const urlToCopy = shareUrl || `${window.location.origin}/post/${post?.share_token}`;
      const textArea = document.createElement('textarea');
      textArea.value = urlToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShareUrlCopied(true);
      setTimeout(() => setShareUrlCopied(false), 2000);
    }
  };

  const handleDownloadImage = async () => {
    if (!post?.current_image_url) {
      showToast.error(isRTL ? 'لا توجد صورة للتحميل' : 'No image to download');
      return;
    }

    try {
      let mediaUrl = getImageUrl(post.current_image_url);

      if (
        mediaUrl.startsWith('/') ||
        mediaUrl.startsWith('http://localhost') ||
        mediaUrl.startsWith('https://')
      ) {
        const response = await fetch(mediaUrl, {
          method: 'GET',
          credentials: 'include',
          mode: 'cors',
        });

        if (!response.ok) {
          const a = document.createElement('a');
          a.href = mediaUrl;
          a.download = '';
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          showToast.success(isRTL ? 'تم فتح الصورة' : 'Image opened');
          return;
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const mediaType = getMediaTypeFromUrl(mediaUrl);
        let extension = 'jpg';

        if (mediaType === 'video') {
          extension = blob.type.includes('mp4')
            ? 'mp4'
            : blob.type.split('/')[1] || 'mp4';
        } else if (blob.type && blob.type !== 'application/octet-stream') {
          extension = blob.type.split('/')[1] || 'jpg';
        } else {
          const urlExtension = mediaUrl.split('.').pop()?.split('?')[0];
          extension =
            urlExtension &&
            ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(urlExtension.toLowerCase())
              ? urlExtension
              : 'jpg';
        }

        const sanitizedTitle = (post.title || 'post')
          .replace(/[^a-z0-9]/gi, '_')
          .substring(0, 50);
        a.download = `${sanitizedTitle}-${Date.now()}.${extension}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showToast.success(isRTL ? 'تم التحميل بنجاح' : 'Downloaded successfully');
      } else {
        const a = document.createElement('a');
        a.href = mediaUrl;
        a.download = '';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showToast.info(isRTL ? 'تم فتح الصورة في نافذة جديدة' : 'Image opened in new tab');
      }
    } catch (err) {
      console.error('Download error:', err);
      try {
        const mediaUrl = getImageUrl(post.current_image_url);
        const a = document.createElement('a');
        a.href = mediaUrl;
        a.download = '';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        showToast.info(isRTL ? 'تم فتح الصورة' : 'Image opened');
      } catch (fallbackErr) {
        console.error('Fallback download error:', fallbackErr);
        showToast.error(isRTL ? 'فشل تحميل الملف' : 'Failed to download file');
      }
    }
  };

  const handleDownloadCaption = () => {
    if (!post?.current_caption) {
      showToast.error(isRTL ? 'لا يوجد عنوان للتحميل' : 'No caption to download');
      return;
    }

    try {
      const sanitizedTitle = (post.title || 'post')
        .replace(/[^a-z0-9]/gi, '_')
        .substring(0, 50);
      const blob = new Blob([post.current_caption], {
        type: 'text/plain;charset=utf-8',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizedTitle}-caption-${Date.now()}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success(isRTL ? 'تم تحميل العنوان' : 'Caption downloaded');
    } catch (err) {
      console.error('Download caption error:', err);
      showToast.error(isRTL ? 'فشل تحميل العنوان' : 'Failed to download caption');
    }
  };

  const handleDownloadAll = async () => {
    try {
      showToast.info(isRTL ? 'جاري إعداد الملفات...' : 'Preparing files...');

      if (post.current_image_url) {
        await handleDownloadImage();
      }
      if (post.current_caption) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        handleDownloadCaption();
      }

      showToast.success(isRTL ? 'تم تحميل الملفات' : 'Files downloaded');
    } catch (err) {
      console.error('Download all error:', err);
      showToast.error(isRTL ? 'فشل تحميل الملفات' : 'Failed to download files');
    }
  };

  const handleAddComment = async (commentData) => {
    try {
      await commentsAPI.create(id, commentData);
      await fetchPostData();
      showToast.success(
        isRTL ? 'تم إضافة التعليق بنجاح!' : 'Comment added successfully!'
      );
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error || !post) {
    return (
      <div className="max-w-4xl mx-auto py-16 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
        <p className="text-red-600 mb-4">
          {error || (isRTL ? 'لم يتم العثور على البوست' : 'Post not found')}
        </p>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          {isRTL ? 'رجوع' : 'Back'}
        </Button>
      </div>
    );
  }

  const platformInfo = post.platform ? PLATFORMS[post.platform] : null;
  const contentTypeInfo = post.content_type ? CONTENT_TYPES[post.content_type] : null;

  const canApproveOrRequest =
    (isAdmin || isClient) && (post.status === 'pending' || post.status === 'changes_requested');

  const isProductContent =
    post.content_type === 'product_article' && post.product_details;
  const isArticleContent =
    post.content_type === 'blog_article' && post.article_details;

  const handleSaveAsDraft = async () => {
    try {
      setActionLoading(true);
      await postsAPI.update(id, { status: 'draft' });
      showToast.success(isRTL ? 'تم الحفظ كمسودة' : 'Saved as draft');
      navigate('/posts');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-5 bg-gradient-to-r from-sky-50 via-white to-emerald-50 border-slate-200">
        <div className="flex items-start justify-between gap-4">
          <div className={`flex items-start gap-3 flex-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-white/80 transition-colors mt-1"
            >
              <ArrowLeft className={`w-5 h-5 text-slate-600 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <div className="inline-flex items-center gap-2 mb-1">
                <ClipboardList className="w-4 h-4 text-sky-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                  {isRTL ? 'تصميم تجريبي C (كامل المزايا)' : 'Experimental Layout C (Full)'}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">
                {post.title || (isRTL ? 'بوست بدون عنوان' : 'Untitled Post')}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                {post.client_name && (
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {post.client_name}
                  </span>
                )}
                {post.created_at && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(post.created_at)}
                  </span>
                )}
                {platformInfo && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/80 border border-slate-200">
                    <PlatformIcon platformId={platformInfo.id} className="w-3 h-3" />
                    <span className="text-[11px]">
                      {platformInfo.icon}{' '}
                      {isRTL ? platformInfo.nameAr : platformInfo.name}
                    </span>
                  </span>
                )}
                {contentTypeInfo && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900 text-white text-[11px]">
                    {contentTypeInfo.icon}
                    {isRTL ? contentTypeInfo.nameAr : contentTypeInfo.name}
                  </span>
                )}
                <StatusBadge status={post.status} />
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <Link to={`/posts/${post.id}`}>
              <Button variant="secondary" size="sm">
                {isRTL ? 'الصفحة الأصلية' : 'Original Page'}
              </Button>
            </Link>
          </div>
        </div>
      </Card>

      {/* Main two-column layout: big design view always visible + actions/info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Design column */}
        <div className="lg:col-span-2">
          <Card className="p-5 rounded-2xl border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-slate-800">
                {isRTL ? 'التصميم' : 'Design'}
              </h2>
            </div>
            <div className="flex items-center justify-center rounded-xl bg-slate-50 border border-slate-200 overflow-hidden min-h-[260px]">
              {post.current_image_url ? (
                <img
                  src={post.current_image_url}
                  alt={post.title || 'Post'}
                  className="max-h-[460px] w-auto object-contain"
                />
              ) : (
                <div className="flex flex-col items-center justify-center text-slate-400 py-10">
                  <ImageIcon className="w-10 h-10 mb-3" />
                  <p className="text-sm">
                    {isRTL
                      ? 'لا توجد صورة حالية لهذا البوست'
                      : 'No current image for this post'}
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* تحت التصميم: إما كابتشن أو تفاصيل حسب نوع المحتوى */}
          <div className="mt-4 space-y-4">
            {isProductContent ? (
              <Card className="p-5 rounded-2xl border-slate-200 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {isRTL ? 'تفاصيل المنتج' : 'Product Details'}
                </h2>
                <pre className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed bg-slate-50 border border-slate-200 rounded-xl p-3">
                  {JSON.stringify(post.product_details, null, 2)}
                </pre>
              </Card>
            ) : isArticleContent ? (
              <Card className="p-5 rounded-2xl border-slate-200 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {isRTL ? 'تفاصيل المقال' : 'Article Details'}
                </h2>
                <pre className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed bg-slate-50 border border-slate-200 rounded-xl p-3">
                  {JSON.stringify(post.article_details, null, 2)}
                </pre>
              </Card>
            ) : (
              <Card className="p-5 rounded-2xl border-slate-200 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {isRTL ? 'الكابتشن الحالي' : 'Current Caption'}
                </h2>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 max-h-64 overflow-y-auto">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                    {post.current_caption ||
                      (isRTL ? 'لا يوجد كابتشن حالي' : 'No current caption')}
                  </p>
                </div>
              </Card>
            )}

            {post.current_notes && (
              <Card className="p-5 rounded-2xl border-slate-200 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {isRTL ? 'ملاحظات' : 'Notes'}
                </h2>
                <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                  {post.current_notes}
                </p>
              </Card>
            )}
          </div>
        </div>

        {/* Right column: platform preview + actions + quick info + comments */}
        <div className="space-y-4">
          {/* Platform Preview */}
          {post.platform && (
            <Card className="p-4 rounded-2xl border-slate-200 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-800 mb-2">
                {isRTL ? 'معاينة على المنصة' : 'Platform Preview'}
              </h2>
              <div className="flex justify-center">
                <PlatformPreview
                  platform={post.platform}
                  contentType={post.content_type}
                  imageUrl={
                    post.current_image_url ? getImageUrl(post.current_image_url) : null
                  }
                  caption={post.current_caption}
                  title={post.title}
                  description={post.description}
                  richDescription={post.rich_description}
                  productDetails={post.product_details}
                  articleDetails={post.article_details}
                  specifications={post.specifications}
                  carouselItems={post.current_carousel_items || []}
                  username={
                    post.platform === 'instagram' &&
                    post.client_social_media_accounts?.instagram
                      ? post.client_social_media_accounts.instagram.replace('@', '')
                      : 'username'
                  }
                />
              </div>
            </Card>
          )}

          {/* Actions */}
          <Card className="p-5 rounded-2xl border-slate-200 shadow-sm space-y-3">
            <h2 className="text-sm font-semibold text-slate-800">
              {isRTL ? 'الإجراءات الرئيسية' : 'Main Actions'}
            </h2>
            <div className="space-y-2">
              {/* Save as Draft */}
              <Button
                onClick={handleSaveAsDraft}
                disabled={actionLoading}
                variant="secondary"
                className="w-full"
              >
                {isRTL ? 'حفظ كمسودة والخروج' : 'Save as Draft & Exit'}
              </Button>

              {/* Schedule placeholder (no logic yet) */}
              <Button disabled variant="outline" className="w-full">
                {isRTL ? 'جدولة (لاحقاً)' : 'Schedule (coming soon)'}
              </Button>

              {canApproveOrRequest && (
                <>
                  <Button
                    onClick={handleApprove}
                    disabled={actionLoading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Check className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    {isRTL ? 'تعميد البوست' : 'Approve Post'}
                  </Button>
                  <Button
                    onClick={handleRequestChanges}
                    disabled={actionLoading}
                    variant="secondary"
                    className="w-full"
                  >
                    {isRTL ? 'طلب تعديلات' : 'Request Changes'}
                  </Button>
                </>
              )}
              {post.status !== 'pending' && (
                <Button
                  onClick={handleMarkAsReadyForReview}
                  disabled={actionLoading}
                  variant="outline"
                  className="w-full"
                >
                  {isRTL ? 'تعليم كجاهز للمراجعة' : 'Mark as Ready for Review'}
                </Button>
              )}
            </div>
          </Card>

          {/* Share & Download */}
          <Card className="p-5 rounded-2xl border-slate-200 shadow-sm space-y-3">
            <h2 className="text-sm font-semibold text-slate-800">
              {isRTL ? 'مشاركة وتحميل' : 'Share & Download'}
            </h2>
            <div className="space-y-2">
              <Button
                onClick={handleCopyShareLink}
                disabled={generatingShareLink}
                variant="secondary"
                className="w-full"
              >
                <Share2 className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {shareUrlCopied
                  ? isRTL
                    ? 'تم نسخ الرابط'
                    : 'Link Copied'
                  : isRTL
                  ? 'نسخ رابط المشاركة'
                  : 'Copy Share Link'}
              </Button>
              <Button onClick={handleDownloadImage} variant="outline" className="w-full">
                <Download className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {isRTL ? 'تحميل الصورة / الفيديو' : 'Download Image / Video'}
              </Button>
              <Button onClick={handleDownloadCaption} variant="outline" className="w-full">
                {isRTL ? 'تحميل الكابتشن' : 'Download Caption'}
              </Button>
              <Button onClick={handleDownloadAll} variant="outline" className="w-full">
                {isRTL ? 'تحميل كل المحتوى' : 'Download All'}
              </Button>
            </div>
          </Card>

          {/* Quick info */}
          <Card className="p-5 rounded-2xl border-slate-200 shadow-sm space-y-2 text-sm text-slate-700">
            <h2 className="text-sm font-semibold text-slate-800 mb-2">
              {isRTL ? 'معلومات إضافية' : 'Additional Info'}
            </h2>
            {post.serial_number && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500">
                  {isRTL ? 'رقم البوست' : 'Post #'}
                </span>
                <span className="font-medium">{post.serial_number}</span>
              </div>
            )}
            {post.schedule_date && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500">
                  {isRTL ? 'تاريخ الجدولة' : 'Scheduled at'}
                </span>
                <span className="font-medium">{formatDate(post.schedule_date)}</span>
              </div>
            )}
            {post.approved_at && (
              <div className="flex items-center justify-between">
                <span className="text-slate-500">
                  {isRTL ? 'تاريخ التعميد' : 'Approved at'}
                </span>
                <span className="font-medium">{formatDate(post.approved_at)}</span>
              </div>
            )}
          </Card>

          {/* Comments */}
          <CommentsList
            comments={comments || []}
            postId={id}
            onAddComment={handleAddComment}
            onRefresh={fetchPostData}
          />
        </div>
      </div>

      {/* Change Request Modal */}
      {showChangeRequestModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">
              {isRTL ? 'طلب تعديلات' : 'Request Changes'}
            </h2>
            <p className="text-sm text-slate-600 mb-3">
              {isRTL
                ? 'اكتب الملاحظات أو التعديلات المطلوبة على هذا البوست'
                : 'Write the requested changes or feedback for this post.'}
            </p>
            <textarea
              value={changeRequestText}
              onChange={(e) => setChangeRequestText(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-sm"
            />
            <div
              className={`mt-4 flex justify-end gap-2 ${
                isRTL ? 'flex-row-reverse' : ''
              }`}
            >
              <Button variant="secondary" onClick={handleCancelChangeRequest}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitChangeRequest}
                disabled={actionLoading}
              >
                {isRTL ? 'إرسال' : 'Submit'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostReviewLayoutC;

